// @givmo/compliance — uploading one document into the receipt vault (CCM-WEB2).
//
// ══ THREE CALLS, AND NOTHING EXISTS UNTIL THE THIRD ════════════════════════════════════════════
//
//   1. issue   — ask for a signed PUT URL. No row is written; nothing has happened yet.
//   2. store   — send the bytes straight to the restricted bucket, bare `fetch`, with every header
//                the signature binds: the grant's own (CCM-R1-UPLOAD-SIZE-CAP), or the two a backend
//                that names none signs. See the host adapter's `putArtifactBytes`.
//   3. confirm — hand the object reference back. The backend checks the object is REALLY THERE and
//                refuses otherwise, and only then does a document exist.
//
// ⚠ THE ORDER IS WHAT MAKES THE RECEIPT RULE HOLD, SO THE COPY MUST NOT SMOOTH IT OVER. A confirm
// refused because the upload never finished has written NOTHING — no partial row, no id resolving to
// nothing. This component says exactly that and offers the upload again; it must never report a
// document as recorded because two of the three calls succeeded.
//
// ⚠ THERE IS NO REPLACE, ANYWHERE, AND THIS COMPONENT MUST NOT INVENT ONE. Re-uploading mints a NEW
// document with a new id; nothing is ever overwritten in place. A "replace" affordance would promise
// a verb the backend deliberately does not have.

import { useRef, useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Icon, testIdProp } from '@givmo/design-system';
import { UploadCloud } from 'lucide-react';
import {
  ARTIFACT_FILE_ACCEPT,
  artifactContentTypeFor,
  decodeConfirmedArtifact,
  decodeUploadGrant,
  isUploadIncomplete,
  isVaultStorageUnavailable,
  isWordRefused,
  serverMessage,
  type ArtifactUploadGrant,
  type ComplianceArtifact,
} from './compliance-decode';
import { useCompliance, type ComplianceOperations } from './compliance-context';

/**
 * What the organization is told when the vault has no storage behind it.
 *
 * ⚠ RECORD-SHAPED AND NON-ALARMING, BOTH DELIBERATE. It says what cannot be done and what is
 * unaffected. It does not say "error", does not invite a retry that cannot succeed, and does not
 * imply anything about documents already recorded — the filings, the recorded facts and every
 * computed date are untouched by the bucket's absence. Its words are Jonathan's (2026-10-01, rulings
 * log item 12), naming Compliance rather than the retired register.
 */
export const VAULT_UNAVAILABLE_COPY =
  'Documents can’t be recorded in this environment yet. Everything already in Compliance is unaffected.';

/** The generic refusal, used when nothing more specific is known. */
const UPLOAD_REFUSED_COPY = 'That document couldn’t be uploaded. Please try again.';

/** A picked file the vault does not take (CCM-R1-INTAKE-WORD: Word files joined). */
export const ARTIFACT_TYPE_REFUSED_COPY = 'Documents have to be a PDF, a Word file or an image.';

/**
 * Said once, under the control, when the upload carried macros and the stored file is the Word file
 * Givmo wrote from it without them (D-CCM-21 Q8; frame 05's approved words). Only when the backend
 * says macros were removed — a `.docm` with none gets no notice.
 */
export const MACROS_REMOVED_COPY =
  'This file had macros. We removed them and saved it as a regular Word file.';

/**
 * Said when the backend refused a Word file it could not remove the macros from, and gave no line of
 * its own. Its lines say which case it was (too large, an embedded object, or unreadable); this is
 * the one true of all three, the same as its generic line.
 */
const WORD_REFUSED_COPY =
  'We couldn’t remove this file’s macros, so it wasn’t saved. Open it in Word, save it as a Word Document (.docx), and upload that.';

/**
 * The fallback copy for a failure that is not a Givmo API error.
 *
 * ⚠ **KEEPS THE STATUS THE STORAGE LEG REPORTED, AND THAT MATTERS MORE HERE THAN IT LOOKS.** The
 * bytes PUT does not go through the api-client, so its failure is a plain `Error` carrying the
 * numeric status {@link putArtifactBytes} put there — and on this path a **403 is the signature**
 * of the two things most likely to be wrong in a fresh environment: a bucket with no CORS policy,
 * or a PUT missing one of the headers bound into the V4 signature. Collapsing that into "please try
 * again" sends a charity to retry forever and tells whoever is diagnosing it nothing.
 *
 * The message is still record-shaped and blames nobody; it just says what happened.
 */
function storageLegMessage(error: unknown): string {
  return error instanceof Error && error.message.trim().length > 0
    ? error.message
    : UPLOAD_REFUSED_COPY;
}

/**
 * The three calls, once, for every upload control (this one and the upload sheet's): issue, store,
 * confirm. Resolves with the recorded document; nothing exists until the confirm returns.
 */
export async function uploadArtifactFile(
  operations: ComplianceOperations,
  orgRef: string | null,
  file: File,
  contentType: string,
): Promise<ComplianceArtifact> {
  if (orgRef === null) throw new Error('We couldn’t start that upload. Please try again.');
  // 1. Issue. The content type is checked against the allow-list here AND bound into the
  //    signature, so a grant for a PDF cannot be reused for anything else. A Word file the
  //    browser left untyped is typed by its extension (`artifactContentTypeFor`).
  const grant = decodeUploadGrant(
    await operations.issueArtifactUploadUrl(orgRef, {
      file_name: file.name,
      content_type: contentType,
    }),
  );
  if (grant === undefined) {
    throw new Error('We couldn’t start that upload. Please try again.');
  }

  // CCM-R1-UPLOAD-SIZE-CAP: a file over the link's limit is one storage refuses, so it is refused
  // here, before any byte is sent.
  if (grant.maxBytes !== undefined && file.size > grant.maxBytes) {
    throw new ArtifactTooLargeError(grant.maxBytes);
  }
  // 2. Store, with exactly the headers the signature binds: the grant's own, or, from a backend that
  //    names none, the two it signs. `grant.contentType` rather than `file.type`: the grant is what
  //    was SIGNED, and a mismatch is a signature failure rather than a validation message.
  await operations.putArtifactBytes(grant.uploadUrl, uploadHeaders(grant), file);

  // 3. Confirm. Nothing is recorded until this returns.
  const artifact = decodeConfirmedArtifact(
    await operations.confirmArtifact(orgRef, {
      object_ref: grant.objectRef,
      file_name: file.name,
      content_type: grant.contentType,
    }),
  );
  if (artifact === undefined || artifact.artifactId.length === 0) {
    throw new Error('That document couldn’t be recorded. Please try again.');
  }
  return artifact;
}

/**
 * The headers a grant's PUT sends: every one its signature binds, as the grant names them, or the
 * two a backend that names none signs (the declared type, and `attachment`). Storage refuses a PUT
 * missing a signed header, so neither set is ever trimmed or added to.
 */
export function uploadHeaders(grant: ArtifactUploadGrant): Record<string, string> {
  return (
    grant.headers ?? { 'Content-Type': grant.contentType, 'Content-Disposition': 'attachment' }
  );
}

/** CCM-R1-UPLOAD-SIZE-CAP: a picked file over the link's limit, refused before it is sent. */
export class ArtifactTooLargeError extends Error {
  constructor(readonly maxBytes: number) {
    super('too large');
  }
}

/**
 * APPROVED (D-CCM-112, Jonathan 2026-10-18 18:13 PDT, "approve recs") (CCM-R1-UPLOAD-SIZE-CAP): said
 * under the control for a file over the link's limit, in whole megabytes, as the limit is set
 * (50 MB).
 */
export function fileTooLargeLine(maxBytes: number): string {
  return `This file is over ${Math.round(maxBytes / (1024 * 1024))} MB, the most Givmo stores for one document.`;
}

/** What an upload's refusal says, by what refused it (see the header for why each is worded so). */
export function uploadRefusalMessage(error: unknown): string {
  if (error instanceof ArtifactTooLargeError) return fileTooLargeLine(error.maxBytes);
  if (isVaultStorageUnavailable(error)) return VAULT_UNAVAILABLE_COPY;
  if (isWordRefused(error)) {
    // The upload finished and the file was refused: its macros could not be removed, so it was
    // not kept. The backend's line says why and what to upload instead; "try again" would be
    // untrue, because the same file is refused every time.
    return serverMessage(error, WORD_REFUSED_COPY);
  }
  if (isUploadIncomplete(error)) {
    // The confirm refused: the object was not in the bucket, or not under this organization's
    // prefix. NOTHING was recorded, and saying so is the honest answer — a charity told
    // "something went wrong" would reasonably assume a half-saved document it must clean up.
    return 'That document didn’t finish uploading, so nothing was recorded. Try again.';
  }
  return serverMessage(error, storageLegMessage(error));
}

export interface ArtifactUploadProps {
  /** Called with the recorded document once all three calls have succeeded. */
  onUploaded: (artifact: ComplianceArtifact) => void;
  /** The control's own label — the surrounding surface says what the document is FOR. */
  label: string;
  /**
   * A line set directly under the button, before anything the upload itself says (frame 05 sets the
   * accepted types under the upload action). The surrounding surface owns its words.
   */
  hint?: ReactNode;
  /**
   * What the file picker offers, where the surface takes less than the vault does
   * (CCM-R1-SIGN-OUTSIDE-B's signed copy: a PDF). The vault's own allow-list otherwise.
   */
  accept?: string;
  testId: string;
}

/**
 * A file picker that puts one document in the vault.
 *
 * Composed by the documents surface (a plain upload) and by the filing-record panel (upload the
 * confirmation you are about to record a filing against), so the three-call sequence is written once.
 */
export function ArtifactUpload({ onUploaded, label, hint, accept, testId }: ArtifactUploadProps) {
  const { orgRef, operations } = useCompliance();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  // The picked file's own name when its macros were removed, so the notice under it says which file
  // (frame 05 sets it under the file's row); `null` when there is nothing to say.
  const [macrosRemovedFrom, setMacrosRemovedFrom] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: ({ file, contentType }: { file: File; contentType: string }) =>
      uploadArtifactFile(operations, orgRef, file, contentType),
    onSuccess: (artifact, { file }) => {
      setRefusal(null);
      setMacrosRemovedFrom(artifact.macrosRemoved ? file.name : null);
      onUploaded(artifact);
    },
    onError: (error: unknown) => setRefusal(uploadRefusalMessage(error)),
  });

  function onPick(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Allow re-picking the same file after a refusal.
    event.target.value = '';
    if (file === undefined) return;

    // ⚠ CHECKED AGAINST THE BACKEND'S OWN ALLOW-LIST, and this is the only refusal this control
    // applies before calling. There is deliberately NO size limit here: the backend sets none for a
    // document it stores as uploaded — it reads the stored size back as a fact rather than validating
    // it — so a cap invented here would refuse a confirmation letter the vault would have accepted.
    // (The one cap is the backend's, on a `.docm` it must rewrite, and it says so itself.)
    const contentType = artifactContentTypeFor(file);
    if (contentType === undefined) {
      setRefusal(ARTIFACT_TYPE_REFUSED_COPY);
      setMacrosRemovedFrom(null);
      return;
    }
    setRefusal(null);
    setMacrosRemovedFrom(null);
    upload.mutate({ file, contentType });
  }

  return (
    <div className="flex flex-col gap-4" {...testIdProp(testId)}>
      <Button
        appearance="saas"
        tone="secondary"
        size="sm"
        loading={upload.isPending}
        leadingIcon={<Icon icon={UploadCloud} size={14} aria-hidden />}
        onClick={() => inputRef.current?.click()}
        testId={`${testId}-open`}
      >
        {label}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept={accept ?? ARTIFACT_FILE_ACCEPT}
        className="hidden"
        onChange={onPick}
        {...testIdProp(`${testId}-input`)}
      />
      {hint}
      {refusal !== null && (
        <p className="text-s12 text-neutral-700" {...testIdProp(`${testId}-refusal`)}>
          {refusal}
        </p>
      )}
      {macrosRemovedFrom !== null && refusal === null && (
        <div className="mt-4 flex flex-col gap-2">
          <span
            className="min-w-0 text-s13 text-neutral-800 wrap-anywhere"
            {...testIdProp(`${testId}-macros-file`)}
          >
            {macrosRemovedFrom}
          </span>
          <p className="text-s12 text-neutral-600" {...testIdProp(`${testId}-macros-removed`)}>
            {MACROS_REMOVED_COPY}
          </p>
        </div>
      )}
    </div>
  );
}
